import React, { useMemo } from 'react';
import { SpreadsheetTable } from './SpreadsheetTable';

interface KeyValueDiffViewerProps {
  oldData: Record<string, any> | null | undefined;
  newData: Record<string, any>;
}

function resolveValue(entry: any): string {
  if (entry === null || entry === undefined) return '';
  if (typeof entry === 'object' && 'value' in entry) {
    return formatDisplay(entry.value);
  }
  return formatDisplay(entry);
}

function formatDisplay(value: any): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'number') {
    if (Math.abs(value) < 0.01 && value !== 0) return value.toExponential(3);
    return value.toLocaleString(undefined, { maximumFractionDigits: 4 });
  }
  return String(value);
}

export const KeyValueDiffViewer: React.FC<KeyValueDiffViewerProps> = ({ oldData, newData }) => {
  const { rows, rowClasses } = useMemo(() => {
    const allKeys = new Set<string>();
    if (newData) Object.keys(newData).forEach(k => allKeys.add(k));
    if (oldData) Object.keys(oldData).forEach(k => allKeys.add(k));

    const sortedKeys = Array.from(allKeys).sort();
    const tableRows: [string, string][] = [];
    const classes: (string | undefined)[] = [];

    for (const key of sortedKeys) {
      const inNew = newData && key in newData;
      const inOld = oldData && key in oldData;
      const newVal = inNew ? resolveValue(newData[key]) : '';
      const oldVal = inOld ? resolveValue(oldData![key]) : '';

      if (!oldData) {
        tableRows.push([key, newVal]);
        classes.push(undefined);
      } else if (inNew && !inOld) {
        tableRows.push([key, newVal]);
        classes.push('ss-row-added');
      } else if (!inNew && inOld) {
        tableRows.push([key, oldVal]);
        classes.push('ss-row-removed');
      } else if (newVal !== oldVal) {
        tableRows.push([key, `${newVal}  (was: ${oldVal})`]);
        classes.push('ss-row-changed');
      } else {
        tableRows.push([key, newVal]);
        classes.push(undefined);
      }
    }

    return { rows: tableRows, rowClasses: classes };
  }, [oldData, newData]);

  if (rows.length === 0) {
    return <div className="ss-empty">No worksheet data available.</div>;
  }

  return (
    <SpreadsheetTable
      headers={['Key', 'Value']}
      rows={rows}
      rowClasses={rowClasses}
      showRowNumbers={false}
      maxHeight={400}
    />
  );
};
